import { useEffect, useState, type PropsWithChildren } from "react";
import { NavLink } from "react-router-dom";
import { appConfig } from "../lib/config";
import { plannerSelectionCount, PLANNER_STORAGE_KEY, PLANNER_UPDATED_EVENT } from "../lib/planner-storage";

type ThemePreference = "system" | "light" | "dark";
type PalettePreference = "terracotta" | "jade" | "ocean" | "plum" | "amber";
interface Preferences {
  theme: ThemePreference;
  palette: PalettePreference;
  fontScale: number;
  brightness: number;
}
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const paletteOptions: Array<{ value: PalettePreference; label: string }> = [
  { value: "terracotta", label: "陶土" },
  { value: "jade", label: "翡翠" },
  { value: "ocean", label: "海蓝" },
  { value: "plum", label: "梅紫" },
  { value: "amber", label: "琥珀" },
];

const defaults: Preferences = { theme: "system", palette: "terracotta", fontScale: 1, brightness: 1 };

function initialPreferences(): Preferences {
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem("yukai-v1-preferences") ?? "{}") } as Preferences;
  } catch {
    return defaults;
  }
}

function resolvedTheme(theme: ThemePreference): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppShell({ children }: PropsWithChildren) {
  const [preferences, setPreferences] = useState<Preferences>(initialPreferences);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [plannerCount, setPlannerCount] = useState(plannerSelectionCount);
  const [online, setOnline] = useState(() => navigator.onLine);
  const theme = resolvedTheme(preferences.theme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.palette = preferences.palette;
    document.documentElement.style.setProperty("--font-scale", String(preferences.fontScale));
    document.documentElement.style.setProperty("--brightness", String(preferences.brightness));
    localStorage.setItem("yukai-v1-preferences", JSON.stringify(preferences));
  }, [preferences, theme]);

  useEffect(() => {
    const capture = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPromptEvent); };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  useEffect(() => {
    const updatePlannerCount = () => setPlannerCount(plannerSelectionCount());
    const storage = (event: StorageEvent) => {
      if (event.key === PLANNER_STORAGE_KEY) updatePlannerCount();
    };
    window.addEventListener(PLANNER_UPDATED_EVENT, updatePlannerCount);
    window.addEventListener("storage", storage);
    return () => {
      window.removeEventListener(PLANNER_UPDATED_EVENT, updatePlannerCount);
      window.removeEventListener("storage", storage);
    };
  }, []);

  useEffect(() => {
    const connected = () => setOnline(true);
    const disconnected = () => setOnline(false);
    window.addEventListener("online", connected);
    window.addEventListener("offline", disconnected);
    return () => {
      window.removeEventListener("online", connected);
      window.removeEventListener("offline", disconnected);
    };
  }, []);

  function update(patch: Partial<Preferences>) {
    setPreferences((current) => ({ ...current, ...patch }));
  }

  async function install() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <NavLink className="brand" to="/" aria-label="Yukai Recipe System 首页">
          <span className="brand-mark" aria-hidden="true">食</span>
          <span><strong>Yukai Recipes</strong><small>家庭菜谱与今日菜单</small></span>
        </NavLink>

        <nav className="primary-nav" aria-label="主导航">
          <NavLink to="/" end>菜谱库</NavLink>
          <NavLink to="/planner">今日菜单{plannerCount ? <span className="nav-count" aria-label={`已选 ${plannerCount} 道菜`}>{plannerCount}</span> : null}</NavLink>
          <NavLink to="/admin">管理</NavLink>
        </nav>

        <div className="header-actions">
          {!online ? <span className="offline-pill">离线</span> : null}
          <span className={`mode-pill mode-pill--${appConfig.useApi ? "cloud" : "demo"}`}>{appConfig.useApi ? "D1 云端" : "本地演示"}</span>
          {installPrompt ? <button className="install-button" type="button" onClick={() => void install()}>安装</button> : null}
          <details className="display-settings">
            <summary aria-label="显示设置">显示</summary>
            <div>
              <label>主题<select value={preferences.theme} onChange={(event) => update({ theme: event.target.value as ThemePreference })}><option value="system">跟随系统</option><option value="light">白天</option><option value="dark">夜间</option></select></label>
              <div className="palette-field">
                <span>配色</span>
                <div className="palette-picker" role="radiogroup" aria-label="主题配色">
                  {paletteOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`palette-option${preferences.palette === option.value ? " is-active" : ""}`}
                      data-palette-option={option.value}
                      type="button"
                      role="radio"
                      aria-label={`${option.label}配色`}
                      aria-checked={preferences.palette === option.value}
                      onClick={() => update({ palette: option.value })}
                    >
                      <span className="palette-swatch" aria-hidden="true" />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <label>字号<select value={preferences.fontScale} onChange={(event) => update({ fontScale: Number(event.target.value) })}><option value="0.9">较小</option><option value="1">标准</option><option value="1.12">较大</option><option value="1.25">特大</option></select></label>
              <label>亮度<input type="range" min="0.8" max="1.15" step="0.05" value={preferences.brightness} onChange={(event) => update({ brightness: Number(event.target.value) })} /></label>
              <button type="button" onClick={() => setPreferences(defaults)}>恢复默认</button>
            </div>
          </details>
          <button className="icon-button" type="button" onClick={() => update({ theme: theme === "light" ? "dark" : "light" })} aria-label={theme === "light" ? "切换到夜间模式" : "切换到白天模式"}>{theme === "light" ? "月" : "日"}</button>
        </div>
      </header>
      <main>{children}</main>
      <footer className="site-footer"><span>把家里的味道，整理成可以传下去的做法。</span><span>Yukai Recipe System v1</span></footer>
    </div>
  );
}
